import React from "react";
import { Outlet } from "react-router";
import { Box, Stack, Typography, Link } from "@mui/material";
import { useDevice } from "src/hooks/media-query";
import { useScrollToTop } from "src/hooks/scroll";
import logoWhiteImage from "url:src/assets/images/logo-white.png";
import backgroundImage from "url:src/assets/images/background.jpg";
import Header from "./Header";
import Footer from "./Footer";
import { MAIN_MAX_WIDTH, HEADER_HEIGHT } from "src/styles/const";
import { useIsTopPage } from "src/hooks/useIsTopPage";

export default function Layout() {
  useScrollToTop();
  const { isMobile } = useDevice();
  const isTopPage = useIsTopPage();

  const menuSections = [
    {
      title: "無料売却AI査定",
      items: [
        { label: "特徴", href: "/#features-section" },
        { label: "成約事例", href: "/#case-section" },
        { label: "売却の流れ", href: "/#sell-flow-section" },
        { label: "よくある質問", href: "/#faq-section" },
      ],
    },
    {
      title: "株式会社Propally",
      items: [
        { label: "会社概要", href: "https://propally.co.jp/#company", external: true },
        { label: "お問い合わせ", href: "mailto:info@propally.co.jp" },
        { label: "プライバシーポリシー", href: "https://propally.co.jp/privacypolicy.html", external: true },
      ],
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center" }}>
        <Box
          component="main"
          sx={{
            width: isMobile ? "100%" : `${MAIN_MAX_WIDTH}px`,
            backgroundColor: "white",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 1,
            minHeight: "100vh",
          }}
        >
          <Box sx={{ paddingTop: isTopPage ? 0 : `${HEADER_HEIGHT}px` }}>
            <Outlet />
          </Box>
          <Footer />
        </Box>
      </Box>

      <Header />

      {!isMobile && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: `calc((100% - ${MAIN_MAX_WIDTH}px) / 2)`,
              height: "100vh",
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "left center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "primary.dark",
                opacity: 0.75,
                zIndex: 1,
              },
            }}
          >
            <Box
              component="img"
              src={logoWhiteImage}
              alt="Propally Logo"
              sx={{
                width: "200px",
                position: "relative",
                zIndex: 2,
              }}
            />
          </Box>

          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: `calc((100% - ${MAIN_MAX_WIDTH}px) / 2)`,
              height: "100vh",
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "right center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 0,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "primary.dark",
                opacity: 0.75,
                zIndex: 1,
              },
            }}
          >
            <Stack spacing="60px" zIndex={2}>
              {menuSections.map((section, sectionIndex) => (
                <Stack key={sectionIndex} spacing={2}>
                  <Typography fontSize="20px" fontWeight="bold" letterSpacing="0.13em" color="white">
                    {section.title}
                  </Typography>
                  <Stack spacing={1}>
                    {section.items.map((item, itemIndex) => {
                      // 外部リンクやmailtoはそのまま
                      if (item.external || item.href.startsWith("mailto:")) {
                        return (
                          <Link
                            key={itemIndex}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "white",
                              letterSpacing: "0.13em",
                              fontSize: "20px",
                              "&:hover": { opacity: 0.8 },
                              textDecoration: "none",
                            }}
                          >
                            {item.label}
                          </Link>
                        );
                      }
                      // 内部ハッシュリンクはクエリ維持で遷移
                      const isHashLink = item.href.startsWith("/#");
                      const href = isHashLink
                        ? `${location.pathname}${location.search}${item.href.substring(1)}`
                        : item.href;
                      return (
                        <Link
                          key={itemIndex}
                          href={href}
                          sx={{
                            color: "white",
                            letterSpacing: "0.13em",
                            fontSize: "20px",
                            "&:hover": { opacity: 0.8 },
                            textDecoration: "none",
                          }}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}
